import { useEffect, useState } from "react";
import ApiService from "../../services/api.service";
import "../client/ClientLayout.css";
import "../coach/CoachPages.css";
import "./LeaguePages.css";

// Officials tab — claimed officials (GET /league/officials) plus pending SMS
// invites (GET /league/invites?accountType=official). "Invite Official"
// mints + SMSes a claim link (POST /league/invites); an official only shows
// up in the LeagueSchedulePage assign picker once they've actually claimed
// it, so this is also where a league re-sends or revokes a stuck invite.

function AddOfficialModal({ onClose, onSaved }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copiedNotice, setCopiedNotice] = useState(false);

  const canSave = (firstName.trim() || lastName.trim()) && phoneNumber.trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await ApiService.createLeagueInvite({
        accountType: "official",
        phoneNumber: phoneNumber.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      if (resp.smsSent === false && resp.inviteUrl) {
        await navigator.clipboard?.writeText(resp.inviteUrl).catch(() => {});
        setCopiedNotice(true);
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (copiedNotice) {
    return (
      <div className="coach-modal-backdrop" onClick={onClose}>
        <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="client-h2">Couldn't text the official</h2>
          <p className="client-subtitle">
            The invite link couldn't be delivered by SMS, so we copied it to your clipboard — share it with them yourself.
          </p>
          <div className="coach-modal-actions">
            <button className="client-button" onClick={onSaved}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="client-h2">Invite Official</h2>
        <div className="coach-modal-fields">
          <input className="client-input" placeholder="First name" value={firstName}
            onChange={(e) => setFirstName(e.target.value)} />
          <input className="client-input" placeholder="Last name" value={lastName}
            onChange={(e) => setLastName(e.target.value)} />
          <input className="client-input" placeholder="Phone number" value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)} />
        </div>
        <p className="client-subtitle">We'll text them a link to set up their own login.</p>
        {error && <div className="client-error">{error}</div>}
        <div className="coach-modal-actions">
          <button className="client-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="client-button" onClick={save} disabled={saving || !canSave}>
            {saving ? "Sending…" : "Invite Official"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InviteRow({ invite, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const resend = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await ApiService.resendLeagueInvite(invite.inviteId);
      if (resp.smsSent === false && resp.inviteUrl) {
        await navigator.clipboard?.writeText(resp.inviteUrl).catch(() => {});
        window.alert("Couldn't text the official — the invite link was copied to your clipboard instead.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this invite?")) return;
    setBusy(true);
    setError(null);
    try {
      await ApiService.revokeLeagueInvite(invite.inviteId);
      onChanged();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="coach-history-card">
      <div className="coach-history-row" style={{ cursor: "default" }}>
        <div>
          <div className="coach-history-matchup">
            {`${invite.firstName || ""} ${invite.lastName || ""}`.trim() || invite.phoneNumber}
          </div>
          <div className="coach-history-date">{invite.phoneNumber} · Invite sent</div>
        </div>
        <div className="league-invite-actions">
          <button className="client-button-secondary" onClick={resend} disabled={busy}>Resend</button>
          <button className="league-remove-team" onClick={revoke} disabled={busy}>Revoke</button>
        </div>
      </div>
      {error && <div className="client-error">{error}</div>}
    </div>
  );
}

export default function LeagueOfficialsPage() {
  const [officials, setOfficials] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddOfficial, setShowAddOfficial] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [officialsResp, invitesResp] = await Promise.all([
        ApiService.getLeagueOfficials(),
        ApiService.getLeagueInvites("official"),
      ]);
      setOfficials(officialsResp.officials || []);
      setInvites(invitesResp.invites || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (officialId) => {
    if (!window.confirm("Remove this official from your league?")) return;
    try {
      await ApiService.removeLeagueOfficial(officialId);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      <div className="coach-page-header">
        <h1 className="client-h1">Officials</h1>
        <button className="client-button" onClick={() => setShowAddOfficial(true)}>+ Invite Official</button>
      </div>

      {error && <div className="client-error">{error}</div>}

      {loading ? (
        <div className="client-loading-row">Loading…</div>
      ) : (
        <>
          {invites.length > 0 && (
            <>
              <p className="client-subtitle">Pending invites</p>
              <div className="coach-history-list">
                {invites.map((inv) => (
                  <InviteRow key={inv.inviteId} invite={inv} onChanged={load} />
                ))}
              </div>
            </>
          )}

          {officials.length === 0 && invites.length === 0 ? (
            <div className="client-empty">No officials yet. Invite your first one above.</div>
          ) : officials.length === 0 ? (
            <div className="client-empty">No officials have claimed their invite yet.</div>
          ) : (
            <div className="coach-history-list">
              {officials.map((o) => (
                <div key={o.officialId} className="coach-history-card">
                  <div className="coach-history-row" style={{ cursor: "default" }}>
                    <div>
                      <div className="coach-history-matchup">{o.name || o.email}</div>
                      {o.email && <div className="coach-history-date">{o.email}</div>}
                    </div>
                    <button className="league-remove-team" onClick={() => remove(o.officialId)}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showAddOfficial && (
        <AddOfficialModal
          onClose={() => setShowAddOfficial(false)}
          onSaved={() => { setShowAddOfficial(false); load(); }}
        />
      )}
    </div>
  );
}
